// Contract Access card — the admin-side surface for ACL grant management.
// Only rendered when the viewer canAdmin on this contract (admin /
// global-admin, or an editor linked as owner/manager). Lists current
// grants with Revoke buttons (when the manual source is the only one),
// surfaces auto-owner / auto-manager rows as non-revokable with a 'via
// owner/manager link' note, and shows all-contracts users as read-only
// badges (no per-contract grant exists for them).
//
// Brand discipline:
//   - No emerald on this card — the grant list is already scoped to
//     'these users have access', so per-row success badges would be
//     redundant noise.
//   - No violet — the primary 'Save Changes' button lives on the tab
//     header, not here. The 'Add user grant' button is a quiet
//     secondary affordance.

import { useState } from "react";
import { Trash2, UserPlus } from "lucide-react";
import { Card, Button } from "../common";
import type {
  ContractAclUserEntry,
} from "../../services/api";
import type { OrganisationUser } from "../../types";

interface ContractAccessCardProps {
  accessUsers: ContractAclUserEntry[];
  orgUsers: OrganisationUser[];
  onGrant: (userId: string) => void;
  onRevoke: (userId: string) => void;
  /** Set of userIds already on the contract's people roster. When provided
   *  alongside `onAddAsPerson`, the per-row "Add as person" button is hidden
   *  for users already linked as a person. */
  peopleUserIds?: string[];
  /** Optional handler that appends an access-row user as a person on the
   *  contract in one click. When omitted, the "Add as person" button does
   *  not render at all (backward-compatible for callers that don't need it). */
  onAddAsPerson?: (user: { userId: string; name: string; email: string }) => void;
}

function hasManualSource(entry: ContractAclUserEntry): boolean {
  return entry.sources.includes("manual");
}

function autoSourceLabel(entry: ContractAclUserEntry): string | null {
  if (entry.sources.includes("auto-owner") && entry.sources.includes("auto-manager")) {
    return "via owner + manager link";
  }
  if (entry.sources.includes("auto-owner")) return "via owner link";
  if (entry.sources.includes("auto-manager")) return "via manager link";
  return null;
}

export default function ContractAccessCard({
  accessUsers,
  orgUsers,
  onGrant,
  onRevoke,
  peopleUserIds,
  onAddAsPerson,
}: ContractAccessCardProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const peopleUserIdSet = new Set(peopleUserIds ?? []);

  const grantedUserIds = new Set(accessUsers.map((u) => u.userId));

  // All-contracts users (read-only display): editors/viewers with
  // contractAccess === 'all'. Admins and global-admins are hidden
  // entirely — they always have access by role, so listing them in the
  // per-contract scope is noise.
  const allContractsUsers = orgUsers.filter((u) => {
    if (u.role === "admin" || u.role === "global-admin") return false;
    return u.contractAccess === "all";
  });

  // Pickable: selected-access non-admin users who don't already hold a grant.
  const pickableUsers = orgUsers.filter(
    (u) =>
      u.contractAccess === "selected" &&
      u.role !== "admin" &&
      u.role !== "global-admin" &&
      !grantedUserIds.has(u.userId),
  );

  const isEmpty = accessUsers.length === 0 && allContractsUsers.length === 0;

  return (
    <Card padding="none">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-h3 text-slate-900 dark:text-slate-100">Access</h3>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Manage which users with scoped access can see this contract.
            Admins always have access automatically.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={<UserPlus className="h-4 w-4" />}
          onClick={() => setPickerOpen((open) => !open)}
          aria-expanded={pickerOpen}
        >
          Add user grant
        </Button>
      </div>

      {pickerOpen && (
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          {pickableUsers.length === 0 ? (
            <p className="text-body-sm text-slate-500 dark:text-slate-400">
              No scoped users available. Only users with Contract access set
              to &lsquo;Selected&rsquo; on their org-user record show up
              here. Set a user&rsquo;s contract access from the org-admin
              settings page.
            </p>
          ) : (
            <>
              <p className="text-body-sm text-slate-700 dark:text-slate-300 mb-2">
                Grant a user access to this contract. Pick from users with
                scoped (Selected) contract access:
              </p>
              <ul
              role="listbox"
              aria-label="Pick a user to grant"
              className="max-h-48 overflow-y-auto divide-y divide-slate-200 dark:divide-slate-700 rounded-md border border-slate-200 dark:border-slate-700"
            >
              {pickableUsers.map((u) => (
                <li
                  key={u.userId}
                  role="option"
                  aria-selected="false"
                  aria-label={u.name}
                  tabIndex={0}
                  onClick={() => {
                    onGrant(u.userId);
                    setPickerOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onGrant(u.userId);
                      setPickerOpen(false);
                    }
                  }}
                  className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 focus:outline-hidden focus:bg-slate-50 dark:focus:bg-slate-800"
                >
                  <span className="text-body-sm text-slate-700 dark:text-slate-300">
                    {u.name}
                  </span>
                  <span className="text-caption text-slate-500 dark:text-slate-400">
                    {u.email}
                  </span>
                </li>
              ))}
              </ul>
            </>
          )}
        </div>
      )}

      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
        <thead className="bg-slate-50 dark:bg-slate-800">
          <tr>
            <th scope="col" className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Name
            </th>
            <th scope="col" className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Email
            </th>
            <th scope="col" className="px-4 py-2 text-left text-overline uppercase text-slate-500 dark:text-slate-400">
              Source
            </th>
            <th scope="col" className="px-4 py-2 text-right text-overline uppercase text-slate-500 dark:text-slate-400">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {accessUsers.map((u) => {
            const manual = hasManualSource(u);
            const autoLabel = autoSourceLabel(u);
            const canRevoke = manual;
            return (
              <tr key={u.userId}>
                <td className="px-4 py-3 text-body-sm text-slate-900 dark:text-slate-100">{u.name}</td>
                <td className="px-4 py-3 text-body-sm text-slate-500 dark:text-slate-400">{u.email}</td>
                <td className="px-4 py-3 text-body-sm text-slate-500 dark:text-slate-400">
                  {manual && <span>Manual grant</span>}
                  {manual && autoLabel && <span> &middot; </span>}
                  {autoLabel && (
                    <span className="text-caption text-slate-500 dark:text-slate-400">
                      {autoLabel}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {onAddAsPerson && !peopleUserIdSet.has(u.userId) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<UserPlus className="h-4 w-4" />}
                        aria-label={`Add ${u.name} as person on this contract`}
                        onClick={() =>
                          onAddAsPerson({
                            userId: u.userId,
                            name: u.name,
                            email: u.email,
                          })
                        }
                      />
                    )}
                    {canRevoke && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<Trash2 className="h-4 w-4" />}
                        aria-label={`Revoke ${u.name}`}
                        onClick={() => onRevoke(u.userId)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {allContractsUsers.map((u) => (
            <tr key={`all-${u.userId}`} className="opacity-80">
              <td className="px-4 py-3 text-body-sm text-slate-700 dark:text-slate-300">{u.name}</td>
              <td className="px-4 py-3 text-body-sm text-slate-500 dark:text-slate-400">{u.email}</td>
              <td className="px-4 py-3 text-body-sm">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-caption font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                  Has all-contracts access
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {onAddAsPerson && !peopleUserIdSet.has(u.userId) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    icon={<UserPlus className="h-4 w-4" />}
                    aria-label={`Add ${u.name} as person on this contract`}
                    onClick={() =>
                      onAddAsPerson({
                        userId: u.userId,
                        name: u.name,
                        email: u.email,
                      })
                    }
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isEmpty && (
        <p className="px-4 py-6 text-center text-body-sm text-slate-500 dark:text-slate-400">
          No users have been granted access to this contract. Use &lsquo;Add
          user grant&rsquo; to scope a selected-access user in.
        </p>
      )}
    </Card>
  );
}
